/**
 * Full-page Connect / Edit form for one integration. Lives at
 * `/integrations/:integrationKey/connect` and `.../edit`. Integrations like
 * NetSuite SuiteQL have ~13 fields, which overflow a modal — a dedicated route
 * gives them room and a shareable/bookmarkable URL. Reuses the same
 * IntegrationForm the list previously rendered in a modal.
 */
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import { Banner, Skeleton } from '@servicecompressionllc/sc-design-system';
import { AppShell } from '../../components/AppShell.js';
import { useMe } from '../../hooks/useMe.js';
import { findIntegration, useIntegrationCatalog } from './useIntegrationCatalog.js';
import { useIntegrationsListLogic } from './useIntegrationsListLogic.js';
import { IntegrationForm } from './IntegrationForm.js';
import type { IntegrationFormMode } from './useIntegrationFormLogic.js';

interface IntegrationFormPageProps {
  mode: IntegrationFormMode;
}

export const IntegrationFormPage = ({ mode }: IntegrationFormPageProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { integrationKey } = useParams<{ integrationKey: string }>();
  const me = useMe();
  const catalog = useIntegrationCatalog();
  const tenantId = me.data?.tenant.id;

  // Existing config is only needed to prefill the Edit form; skip the fetch
  // entirely in Connect mode (the hook is disabled when tenantId is undefined).
  const list = useIntegrationsListLogic(mode === 'edit' ? tenantId : undefined);

  // Return to wherever the form was opened from — the capability detail (Set up
  // / Configure) or the connected-accounts strip. On a fresh load / deep link
  // (the initial history entry has key 'default') there's no in-app history to
  // pop, so fall back to the integrations home instead of walking out of the app.
  const goBack = (): void => {
    if (location.key === 'default') {
      void navigate('/connections', { replace: true });
    } else {
      void navigate(-1);
    }
  };

  const BackLink = <Breadcrumbs trail={[{ label: 'Back', onClick: goBack }]} />;

  if (me.isLoading || catalog.isLoading || (mode === 'edit' && list.isLoading)) {
    return (
      <AppShell>
        <Skeleton className="h-7 w-64 mb-2" />
        <Skeleton className="h-4 w-96 mb-6" />
        <Skeleton className="h-72 w-full max-w-2xl" />
      </AppShell>
    );
  }

  if (catalog.error) {
    return (
      <AppShell>
        {BackLink}
        <Banner variant="danger">
          <Banner.Body>
            <Banner.Title>Could not load integration catalog.</Banner.Title>
            <Banner.Description>{catalog.error.message}</Banner.Description>
          </Banner.Body>
        </Banner>
      </AppShell>
    );
  }

  /**
   * The route param means different things per mode, mirroring the API:
   *   connect — a catalog KEY, naming which kind to create
   *   edit    — an integration ID, naming which configured row to update
   * A key can have several instances, so it cannot address one row.
   */
  const editingRow =
    mode === 'edit' ? list.data?.integrations.find((row) => row.id === integrationKey) : undefined;

  const definition =
    mode === 'edit'
      ? editingRow && findIntegration(catalog.data, editingRow.key)
      : integrationKey
        ? findIntegration(catalog.data, integrationKey)
        : undefined;

  if (!definition || !tenantId) {
    return (
      <AppShell>
        {BackLink}
        <Banner variant="warning">
          <Banner.Body>
            <Banner.Title>Integration not found</Banner.Title>
            <Banner.Description>No integration matches “{integrationKey}”.</Banner.Description>
          </Banner.Body>
        </Banner>
      </AppShell>
    );
  }

  const prefill = editingRow?.config;
  // Naming an instance only makes sense for a connection: capability sources
  // carry no credentials of their own, so a second one would be meaningless.
  const allowNamedInstance = definition.category === 'connection';

  return (
    <AppShell>
      <div className="max-w-2xl">
        {BackLink}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-ink">
            {mode === 'create' ? 'Connect' : 'Edit'} {editingRow?.name || definition.displayName}
          </h2>
          <p className="text-ink-muted mt-1 text-sm">{definition.description}</p>
        </div>
        <IntegrationForm
          tenantId={tenantId}
          definition={definition}
          mode={mode}
          prefill={prefill}
          integrationId={editingRow?.id}
          allowNamedInstance={allowNamedInstance}
          currentName={editingRow?.name}
          onCancel={goBack}
          onSuccess={goBack}
        />
      </div>
    </AppShell>
  );
};
